"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import posthog from "posthog-js";
import { cn } from "@/lib/utils";
import { getCountryDisplayName } from "@/lib/geo/country-display-name";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { resolveAmericasSwitchTarget, switchChannelPath } from "@/lib/geo/switch-channel-path";
import { useLocaleChannelState } from "@/ui/components/locale-channel/use-locale-channel-state";
import { getLocaleConfig } from "@/config/locale";
import { suppressBrazilCalculatorRegionSuggestion } from "@/lib/geo/brazil-language-suggestion";
import { usePreferredBrowserLanguage } from "@/ui/components/locale-channel/use-preferred-browser-language";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogCloseButton,
} from "@/ui/components/ui/dialog";

type Variant = "welcome" | "mismatch-americas" | "mismatch-worldwide";

export function GeoModal() {
	const t = useTranslations("geoModal");
	const tLC = useTranslations("localeChannel");
	const router = useRouter();
	const pathname = usePathname();
	const state = useLocaleChannelState();
	const [open, setOpen] = useState(false);
	const preferredBrowserLanguage = usePreferredBrowserLanguage();
	const suppressRegionSuggestion = suppressBrazilCalculatorRegionSuggestion(
		pathname,
		state.detectedCountry,
		preferredBrowserLanguage,
	);

	const variant: Variant =
		state.mismatch.kind === "suggest-americas"
			? "mismatch-americas"
			: state.mismatch.kind === "suggest-worldwide"
				? "mismatch-worldwide"
				: "welcome";

	const detectedCountry = state.detectedCountry ?? "";
	const countryName = getCountryDisplayName(detectedCountry, state.currentLocale);

	// Open 800ms after mount if no cookie exists. Having a cookie = user already saw this.
	// Only auto-open for a genuine channel mismatch (suggest-americas/worldwide). The "welcome"
	// variant has nothing to decide for an already-matched visitor (e.g. DE on /de/eu), so showing
	// it as a backdrop modal is pure above-the-fold friction — skip it.
	useEffect(() => {
		if (state.hasGeoPrefCookie || suppressRegionSuggestion) return;
		if (variant === "welcome") return;
		const timer = setTimeout(() => setOpen(true), 800);
		return () => clearTimeout(timer);
	}, [state.hasGeoPrefCookie, variant, suppressRegionSuggestion]);

	// Emit analytics when a mismatch variant of the modal is actually shown.
	useEffect(() => {
		if (!open || suppressRegionSuggestion) return;
		if (variant === "welcome") return;
		posthog.capture("channel_mismatch_suggested", {
			source: "modal",
			fromChannel: state.currentChannel,
			suggestedChannel: variant === "mismatch-americas" ? "us" : "eu",
			detectedCountry,
		});
	}, [open, variant, state.currentChannel, detectedCountry, suppressRegionSuggestion]);

	const writeCookieAndClose = useCallback(
		(overrides: { locale?: string; channel?: string; referenceCurrency?: string } = {}) => {
			setGeoPrefCookie({
				locale: overrides.locale ?? state.currentLocale,
				channel: overrides.channel ?? state.currentChannel,
				referenceCurrency: overrides.referenceCurrency ?? state.referenceCurrency,
			});
			setOpen(false);
		},
		[state.currentLocale, state.currentChannel, state.referenceCurrency],
	);

	const handleWelcomeConfirm = () => {
		writeCookieAndClose();
		router.refresh();
	};

	const handleSwitchToAmericas = () => {
		const target = resolveAmericasSwitchTarget(pathname, state.currentLocale, state.detectedCountry);
		writeCookieAndClose({ locale: target.locale, channel: "us" });
		router.push(target.path);
	};

	const handleSwitchToWorldwide = () => {
		const nextPath = switchChannelPath(pathname, "eu");
		writeCookieAndClose({ channel: "eu" });
		router.push(nextPath);
	};

	const handleStay = () => {
		writeCookieAndClose();
	};

	const handleOpenChange = (v: boolean) => {
		if (!v) handleStay();
		setOpen(v);
	};

	const eyebrow =
		variant === "welcome"
			? t("welcomeEyebrow")
			: variant === "mismatch-americas"
				? t("mismatchAmericasEyebrow")
				: t("mismatchWorldwideEyebrow");

	const title =
		variant === "welcome"
			? t("welcomeTitle", { country: countryName })
			: variant === "mismatch-americas"
				? t("mismatchAmericasTitle", { country: countryName })
				: t("mismatchWorldwideTitle", { country: countryName });

	const description =
		variant === "welcome"
			? t("welcomeDescription")
			: variant === "mismatch-americas"
				? t("mismatchAmericasDescription")
				: t("mismatchWorldwideDescription", { country: countryName });

	const localeConfig = getLocaleConfig(state.currentLocale);
	const regionLabel =
		state.currentChannel.toLowerCase() === "us" ? tLC("regionAmericas") : tLC("regionWorldwide");

	return (
		<Dialog open={open && !suppressRegionSuggestion} onOpenChange={handleOpenChange}>
			<DialogContent
				className={cn(
					"flex max-h-[min(92dvh,42rem)] w-[calc(100vw-1.25rem)] max-w-[min(calc(100vw-1.25rem),26rem)] flex-col gap-0 overflow-hidden p-0",
				)}
				overlayClassName="bg-black/40 backdrop-blur-[2px]"
				data-geo-modal=""
			>
				<div
					className={cn(
						"border-border/60 relative border-b bg-gradient-to-b to-background px-6 pb-5 pt-6",
						variant === "welcome" ? "from-muted/40" : "from-orange-50",
					)}
				>
					<DialogCloseButton className="absolute right-3 top-3" aria-label={t("closeAria")} />
					<DialogHeader className="space-y-1 pr-6 text-left">
						<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
							{eyebrow}
						</p>
						<DialogTitle className="text-base font-semibold">{title}</DialogTitle>
						<DialogDescription className="text-sm text-muted-foreground">{description}</DialogDescription>
					</DialogHeader>
				</div>

				<div className="flex flex-col gap-2 px-6 py-4">
					<SummaryRow label={tLC("languageLabel")} value={localeConfig.nativeName} />
					<SummaryRow label={tLC("regionLabel")} value={regionLabel} />
					<SummaryRow label={tLC("currencyLabel")} value={state.referenceCurrency} />
				</div>

				<div className="border-border/60 bg-muted/20 border-t px-6 py-4">
					{variant === "welcome" ? (
						<button
							type="button"
							onClick={handleWelcomeConfirm}
							className="hover:bg-primary/90 min-h-11 w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-colors"
						>
							{t("confirmPrimary")}
						</button>
					) : variant === "mismatch-americas" ? (
						<button
							type="button"
							onClick={handleSwitchToAmericas}
							className="hover:bg-primary/90 min-h-11 w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-colors"
						>
							{t("switchToAmericas")}
						</button>
					) : (
						<button
							type="button"
							onClick={handleSwitchToWorldwide}
							className="hover:bg-primary/90 min-h-11 w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-colors"
						>
							{t("switchToWorldwide")}
						</button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

function SummaryRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="border-border/60 bg-muted/30 flex items-center justify-between rounded-lg border px-3 py-2.5">
			<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
				{label}
			</span>
			<span className="text-sm font-semibold text-foreground">{value}</span>
		</div>
	);
}
